import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import moment from "moment";
import { useMemo } from "react";
import { useTableSizes } from "../../hooks/use-table-sizes";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export const formatLastActivity = (
  updatedAt: string,
  short: boolean = false,
): string => {
  const now = new Date();
  const updated = new Date(updatedAt);
  const diffMs = now.getTime() - updated.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  // Just now (under 1 min)
  if (diffMinutes < 1) {
    return "Just now";
  }

  // X minutes ago
  if (diffMinutes < 60) {
    return short ? `${diffMinutes}m ago` : `${diffMinutes} min ago`;
  }

  // X hours ago
  if (diffHours < 24) {
    return short ? `${diffHours}h ago` : `${diffHours} hr ago`;
  }

  // Yesterday (check if it was yesterday)
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday =
    updated.getDate() === yesterday.getDate() &&
    updated.getMonth() === yesterday.getMonth() &&
    updated.getFullYear() === yesterday.getFullYear();

  if (isYesterday) {
    return "Yesterday";
  }

  // X days ago
  if (diffDays < 7) {
    return short ? `${diffDays}d ago` : `${diffDays} days ago`;
  }

  // Weeks
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks === 1) {
    return "Last week";
  }
  if (diffWeeks <= 4) {
    return short ? `${diffWeeks}w ago` : `${diffWeeks} weeks ago`;
  }

  // Months
  const diffMonths =
    (now.getFullYear() - updated.getFullYear()) * 12 +
    (now.getMonth() - updated.getMonth());

  if (diffMonths === 1) {
    return "Last month";
  }
  if (diffMonths === 2) {
    return short ? "2mo ago" : "2 months ago";
  }

  // Beyond 3 months: [Month Day, Year]
  return `${MONTHS[updated.getMonth()]} ${updated.getDate()}, ${updated.getFullYear()}`;
};

export const LastActivity = ({
  updatedAt,
  className,
  fluid = false,
}: {
  updatedAt: string;
  className?: string;
  /**
   * Drops the table column's fixed width. In a card's meta line the text
   * shares the row with a pill and truncates to what is left.
   */
  fluid?: boolean;
}) => {
  const { sizes, view } = useTableSizes();
  const formatted = useMemo(
    () => formatLastActivity(updatedAt, view === "home"),
    [updatedAt, view],
  );

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span
          className={cn(
            "tw-block tw-shrink-0 tw-cursor-default tw-px-1.5 tw-font-sans tw-text-sm tw-font-normal tw-text-blue-500",
            className,
          )}
          style={fluid ? undefined : { width: sizes.activity }}
        >
          {formatted}
        </span>
      </Tooltip.Trigger>
      <Tooltip.Content>
        <Tooltip.Text>
          {moment(updatedAt).format("MMMM D, YYYY, h:mm A")}
        </Tooltip.Text>
      </Tooltip.Content>
    </Tooltip>
  );
};
